import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DestroyRef } from '@angular/core';
import Swal from 'sweetalert2';
import { Products } from 'src/app/models/Product.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { ProductsService } from 'src/app/services/products.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * ProductPositionComponent — sub-modal opened from ProductDetail
 * to add a new warehouse position (corsia / ripiano / posizione /
 * quantità + photo) to a product.
 *
 * Phase 1.5 / v2.7.0 — TS cleaned up:
 *   • dropped kendo FileRestrictions (using native input type="file"),
 *   • dropped unused uploadSaveUrl / uploadRemoveUrl / numeProdus,
 *   • dropped onChange / onUpload (kendo-upload helpers — not
 *     used by the new template),
 *   • dropped getAvatarByURL (empty body),
 *   • dropped triggerRefresh stub (all commented out — refresh is
 *     driven by the parent component via the `modalClosed` event),
 *   • dropped ~6 console.log,
 *   • switch to inject() + takeUntilDestroyed,
 *   • added `previewSrc` to show the picked image inline,
 *   • added explicit form validation in addProductPos().
 */
@Component({
  selector: 'app-product-position',
  templateUrl: './product-position.component.html',
  styleUrls: ['./product-position.component.scss'],
  standalone: false,
})


export class ProductPositionComponent implements OnInit {

  private readonly productSrv = inject(ProductsService);
  private readonly destroyRef = inject(DestroyRef);
  public readonly openSrv = inject(OpenModalService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Output() modalClosed = new EventEmitter<void>();
  @Input() product!: Products.IProductDetails;
  @Input() openPhoto = false;

  public addProductPositionForm!: FormGroup;
  public productPosition: Products.IProductPosition | null = null;
  public previewSrc: string | null = null;
  public loading = false;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.addProductPositionForm = new FormGroup({
      pr_description: new FormControl(''),
      pr_b64_image_position: new FormControl('', [Validators.required]),
      pr_image_position: new FormControl(''),
      pr_lane: new FormControl('', [Validators.required]),
      pr_shelf: new FormControl('', [Validators.required]),
      pr_position: new FormControl('', [Validators.required]),
      pr_qty: new FormControl('', [Validators.required]),
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // File upload.
  // ──────────────────────────────────────────────────────────────────

  /** Reads the picked image into a base64 string, stores it in the
   *  in-memory `productPosition` payload + the reactive form (so the
   *  Required validator on `pr_b64_image_position` passes), and
   *  surfaces a preview for the user. */
  onFileLoad(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    this.productPosition = {
      pr_description: '',
      product_position_id: '0',
      pr_code: '',
      product_id: '0',
      pr_image_position: '',
      pr_b64_image_position: '',
      pr_lane: '',
      pr_shelf: '',
      pr_position: '',
      pr_qty: 0,
    };

    const reader = new FileReader();
    reader.readAsDataURL(input.files[0]);
    reader.onload = () => {
      const dataUrl = reader.result?.toString() ?? '';
      this.previewSrc = dataUrl;
      // Strip the "data:image/...;base64," prefix (23 chars in the
      // legacy code — kept identical for backend contract).
      const stripped = dataUrl.substring(23);
      if (this.productPosition) {
        this.productPosition.product_id = this.product?.product_id ?? '0';
        this.productPosition.pr_b64_image_position = stripped;
        this.productSrv.productAvatar = this.productPosition;
      }
      this.addProductPositionForm.patchValue({
        pr_b64_image_position: stripped,
      });
    };
  }

  // ──────────────────────────────────────────────────────────────────
  // Submit.
  // ──────────────────────────────────────────────────────────────────

  addProductPos(): void {
    if (this.addProductPositionForm.invalid || !this.product) {
      this.addProductPositionForm.markAllAsTouched();
      return;
    }
    this.loading = true;
    const value = this.addProductPositionForm.value;
    const payload: Products.IProductPosition = {
      product_id: this.product.product_id,
      pr_code: this.product.pr_code,
      pr_description: this.product.pr_description,
      product_position_id: value.product_position_id ?? '0',
      pr_b64_image_position: this.productPosition?.pr_b64_image_position ?? '',
      pr_image_position: value.pr_image_position ?? '',
      pr_lane: value.pr_lane,
      pr_shelf: value.pr_shelf,
      pr_position: value.pr_position,
      pr_qty: value.pr_qty,
    };

    this.productSrv
      .addProductPosition(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: IWsResult) => {
          this.loading = false;
          this.modalClosed.emit();
          this.openSrv.offPos();
          Swal.fire({
            icon: 'success',
            title: 'Aggiunta!',
            text: 'Posizione aggiunta con successo.',
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: 'Impossibile aggiungere la posizione.',
          });
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  close(): void {
    this.openSrv.offPos();
  }

  goBack(): void {
    this.close();
  }
  
}
